import { MentorDto } from "../types/membersTypes";
import {
  GenerationDto,
  InternalProjectDto,
  MandatoryWorkshop,
  TraineeChoiceDto,
  TraineeMotivationDto,
  TraineeProgressOverviewDto,
  UpdateVotingDeadlinesRequest,
} from "../types/traineesTypes";
import api from "../utils/api";
import { AxiosResponse } from "axios";

/**
 * Get the choices of mentor, internal project and department of all trainees of a given generation
 * @param generationId - The ID of the generation
 * @returns An array of trainee choices of type TraineeChoiceDto
 */
export const getTraineeChoicesOfGeneration = (generationId: number): Promise<AxiosResponse<TraineeChoiceDto[]>> => {
  return api.get<TraineeChoiceDto[]>(`/trainees/generations/${generationId}/trainee-choices`);
};
/**
 * Get the trainee preferences
 * @returns The trainee preferences
 */
export const getTraineePreferences = (memberId: number): Promise<AxiosResponse<TraineeChoiceDto>> => {
  return api.get<TraineeChoiceDto>(`/trainees/${memberId}/trainee-choices`);
};

/**
 * Get the current generation of trainees
 * @returns The current generation of trainees
 */
export const getCurrentGeneration = async (): Promise<AxiosResponse<GenerationDto>> => {
  return api.get<GenerationDto>("/trainees/generations/current");
};

/**
 * Get all generations
 * @returns An array of all generations of type Generation
 */
export const getGenerations = async (): Promise<AxiosResponse<GenerationDto[]>> => {
  return api.get<GenerationDto[]>("/trainees/generations");
};

/**
 * Get the motivation of all trainees of a given generation
 * @param generationID - The ID of the generation
 * @returns The motivation of all trainees of the given generation of type TraineeMotivationDto
 */
export const getTraineeMotivation = async (generationID: number): Promise<AxiosResponse<TraineeMotivationDto>> => {
  return api.get<TraineeMotivationDto>(`/trainees/generations/${generationID}/motivation`);
};

/**
 * Get the mentors of a given generation
 * @param generationID - The ID of the generation
 * @returns An array of mentors of type MentorDto
 */
export const getMentorsOfGeneration = (generationID: number): Promise<AxiosResponse<MentorDto[]>> => {
  return api.get<MentorDto[]>(`/trainees/generations/${generationID}/mentors`);
};

/**
 * Get the internal projects of a given generation
 * @param generationID - The ID of the generation
 * @returns An array of internal projects of type InternalProjectDto
 */
export const getInternalProjectsOfGeneration = (generationID: number): Promise<AxiosResponse<InternalProjectDto[]>> => {
  return api.get<InternalProjectDto[]>(`/trainees/generations/${generationID}/internal-projects`);
};

/**
 * Get the trainee progress with the internal project milestones and mandatory workshops feedback
 * @param generationID - The ID of the generation
 * @returns The trainee progress of type TraineeProgressOverviewDto
 */
export const getTraineeProgress = (generationID: number): Promise<AxiosResponse<TraineeProgressOverviewDto[]>> => {
  return api.get<TraineeProgressOverviewDto[]>(`/trainees/generations/${generationID}/trainee-overview`);
};

/**
 * Get the mandatory workshops of a given generation
 * @param generationID - The ID of the generation
 * @returns An array of mandatory workshops of type MandatoryWorkshop
 */
export const getMandatoryWorkshopsOfGeneration = (
  generationID: number
): Promise<AxiosResponse<MandatoryWorkshop[]>> => {
  return api.get<MandatoryWorkshop[]>(`/trainees/generations/${generationID}/mandatory-workshops`);
};

//-----------------------------------------------------------------------------------------------------------------------
// UPDATE ROUTES

/**
 * Update the trainee preferences
 * @param preferences - The trainee preferences to update
 * @returns void
 */
export const updateTraineePreferences = (
  memberId: number,
  preferences: TraineeChoiceDto
): Promise<AxiosResponse<void>> => {
  return api.put(`/trainees/${memberId}/trainee-choices`, preferences);
};

/**
 * Set the election deadlines of a generation
 * @param electionDeadlinesData - The election deadlines data and generation ID
 * @returns void
 */
export const setElectionDeadline = (electionDeadlinesData: UpdateVotingDeadlinesRequest): Promise<AxiosResponse> => {
  const { generationId, ...electionDeadlines } = electionDeadlinesData;
  return api.post(`/trainees/generations/${generationId}/set-deadline`, electionDeadlines);
};

/**
 * Add a mentor to a generation
 * @param generationId - The ID of the generation
 * @param mentorId - The ID of the mentor to add
 * @returns void
 */
export const addMentor = (generationId: number, mentorId: number): Promise<AxiosResponse> => {
  return api.post(`/trainees/generations/${generationId}/mentors/${mentorId}`);
};

/**
 * Remove a mentor from a generation
 * @param generationId - The ID of the generation
 * @param mentorId - The ID of the mentor to remove
 * @returns void
 */
export const removeMentor = (generationId: number, mentorId: number): Promise<AxiosResponse> => {
  return api.delete(`/trainees/generations/${generationId}/mentors/${mentorId}`);
};
